//------------------------------Start of CheckRewards.js-----------------------------------------------------------
//-----------------------------------------------------------------------------------------------------------------
// Importing Libraries
import React, { useState, useEffect} from "react";
import { Helmet } from "react-helmet";
import { inject } from "@vercel/analytics";
import { motion } from "framer-motion";
import * as Yup from "yup";

// Importing Twitter Functions
import { handleTwitterOAuthCallback, redirectToTwitterAuth, getTwitterUserFromBackend, getUserRewardPosition, submitUserWallet } from "../CheckRewards";

//Importing Components
import Footer from "../components/Footer";

// Form validation schema
const ContactSchema = Yup.object().shape({
  email: Yup.string().email("Email incorrect").required("Adresse email requis"),
});

//-----------------------------------------------------------------------------------------------------------------
// React Main Body
const CheckRewards = () => {

  //---------------------------------------------------------------------------------------------------------------
  // State variables
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [twitterHandle, setTwitterHandle] = useState(null);
  const [rewardData, setRewardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addressError, setAddressError] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  //-----------------------------------------------------------------------------------------------------------------
  // UseEffect Methods
  //-----------------------------------------------------------------------------------------------------------------

  //-----------------------------------------------------------------------------------------------------------------
  // Injecting Vercel Analytics
  useEffect(() => {
    inject();
  }, []);

  //-----------------------------------------------------------------------------------------------------------------
  // Handling Twitter OAuth callback and fetching user data
  useEffect(() => {
    async function completeAuth() {

      await handleTwitterOAuthCallback(setIsAuthenticated);

      const user = await getTwitterUserFromBackend();

      if (user?.username) {
        setTwitterHandle(user.username);
        const reward = await getUserRewardPosition(user.username);
        
        if( reward && reward.Position && reward.Reward && reward.blockchain && reward.walletAddress !== undefined) {
          const normalized = {
            blockchain: reward.blockchain,
            position: reward.Position,
            reward: reward.Reward,
            walletAddress: reward.walletAddress,
            xUserName: reward.xUserName,
          };
          setRewardData(normalized);
          setHasSubmitted(true);
        }else if (reward && reward.Position && reward.Reward !== undefined) {
          const normalized = {
            position: reward.Position,
            reward: reward.Reward,
            xUserName: reward.xUserName,
          };

          setRewardData(normalized);
        } else {
          setRewardData(null);
        }
      }
      setPageLoading(false);
    }
    completeAuth();
  }, []);

  //-------------------------------------------------------------------------------------------------------------------
  // Methods
  //-------------------------------------------------------------------------------------------------------------------

  //-------------------------------------------------------------------------------------------------------------------
  // Basic check for empty string
  const validateAddress = (address) => {
    return address && address.trim() !== "";
  };

  //-------------------------------------------------------------------------------------------------------------------
  // Event Trigger Methods
  //-------------------------------------------------------------------------------------------------------------------

  //-------------------------------------------------------------------------------------------------------------------
  // Method to submit the wallet address to the database
  const handleSubmit = async (e) => {
    e.preventDefault();

    const address = e.target.address.value.trim();
    const blockchain = e.target.blockchain.value;

    setAddressError("");

    if (!validateAddress(address)) {
      setAddressError("Please enter a valid wallet address.");
      return;
    }

    setLoading(true);

    try {
      const result = await submitUserWallet({
        username: twitterHandle,
        walletAddress: address,
        blockchain: blockchain,
      });

      if (result === 'invalid address') {
        setAddressError("Invalid wallet address");
      } else if (result?.success) {
        setHasSubmitted(true);
      } else {
        setAddressError("Failed to submit wallet address.");
      }
    } catch (error) {
      setAddressError("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  //-------------------------------------------------------------------------------------------------------------------
  // Redirects to Twitter authentication
  const handleLogin = (e) => {
    e.preventDefault();
    redirectToTwitterAuth();
  };

  //-------------------------------------------------------------------------------------------------------------------
  // Alt React Component: Redirects while page loads for UseEffect
 if (pageLoading) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white">
      {/* Animated Logo */}
      <motion.img
        src="/assets/Legendsofthepast_Logo_White.svg"
        alt="Legends of the Past Logo"
        className="w-52 lg:w-72 mb-6"
        animate={{
          opacity: [0.4, 1, 0.4],
          y: [0, -5, 0],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </div>
  );
}
  
  //-------------------------------------------------------------------------------------------------------------------
  // Main React Component
  return (
    <section className="overflow-hidden">
      <Helmet title="Legends Of The Past">
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-CZENPTTH1C" />
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-CZENPTTH1C');
          `}
        </script>
      </Helmet>

      {/* Header logo */}
      <figure className="lg:absolute z-50 flex items-end top-0 w-full border-t-[40px] lg:border-t-[80px] border-t-black">
        <a href="/">
          <motion.img
            whileHover={{ scale: 1.1 }}
            src="/assets/Icon_legends_of_the_past.svg"
            title="LEGENDS OF THE PAST"
            alt="Logo"
            className="absolute z-50 w-14 -top-1 lg:w-28 lg:-top-24 lg:left-0"
          />
        </a>
      </figure>

      {/* Conditional Check if user is logged in*/}      
      {!isAuthenticated ? (
        //Section for not logged in users
        <section className="bg-black text-white py-60 text-center lg:flex-row lg:items-start overflow-hidden lg:justify-between bg-gradient-to-br text-white from-[#014EFB] to-[#2AAEF5]">
          <h2 className="text-3xl font-bold">
            Congratulations on participating in the past event
          </h2>
          <p className="mt-4">
            Please log into your X (Twitter) account to receive your potential rewards
          </p>
          
          <form onSubmit={handleLogin} className="mt-6 max-w-sm mx-auto flex flex-col gap-4">
            <button
              type="submit"
              className="mt-2 px-6 py-3 bg-[#1DA1F2] text-white font-semibold rounded hover:bg-[#1A91DA] transition"
            >
              Sign in with Twitter
            </button>
          </form>
        </section>
      ) : (
        <section className="w-full relative pb-10 lg:pb-0 2xl:-mb-10 before:md:block before:absolute lg:before:-top-24 before:-top-12 before:md:-top-0 before:left-[50%] before:md:left-[70%] lg:before:left-[12%] z-0 before:w-2/3 before:md:w-1/3 before:h-full before:content[''] before:bg-contain before:bg-no-repeat before:bg-top">
          <img
            src="/assets/INFINTY_JAR_BACKGROUND.jpg"
            className="lg:-ml-[2rem] lg:h-[35rem] 2xl:-ml-[15rem] 2xl:h-[50rem] 3xl:-ml-[4rem] w-auto top-0"
          />

          <motion.img
            src="/assets/INFINTY_JAR.png"
            animate={{
              y: ["1vw", "-3vw", "1vw"],
              transition: {
                duration: 10,
                times: [0, 0.5, 1],
                repeatType: "loop",
                repeat: Infinity,
                type: "tween",
                damping: 25,
                stiffness: 500,
              },
            }}
            className="absolute lg:-ml-[2rem] lg:h-[35rem] 2xl:-ml-[15rem] 2xl:h-[50rem] 3xl:-ml-[4rem] w-auto top-0"
          />
              
            {/*Conditional for winners or losers*/}
            {rewardData ? (
              <>
              <div className="relative lg:absolute top-6 sm:top-10 md:top-16 lg:top-24 xl:top-28 2xl:top-32 right-0 z-30 lg:mr-28 2xl:mr-20 3xl:mr-28">
                <h2 className="uppercase text-shadow--blue text-3xl sm:text-4xl md:text-5xl lg:text-[2.5rem] 2xl:text-6xl text-white font-bold italic text-center px-6 mt-4">
                  🏆 Welcome,{"  "}
                  <span className="text-inherit italic font-bold text-white">
                    <span
                    style={{
                      display: "inline-block",
                      animation: "bounceShadow 1.6s ease-in-out infinite",
                      textShadow: "0 1px 2px #F5E748, 0 0 2px #F5E748",
                    }}
                    >
                      @{twitterHandle || "loading"}
                    </span>
                  </span>
                </h2>

                <aside className="text-base text-white top-0 right-0">
                  <p className="font-montserrat w-full sm:max-w-2xl mx-auto text-center md:text-justify font-normal px-6 lg:px-14 text-[1rem] 2xl:text-[1.6rem] 2xl:px-2 mt-6 lg:mt-10">
                     Through your domination and determination, you finished: {""}<br/>
                      <span
                      style={{
                        display: "inline-block",
                        animation: "bounceShadow 1.6s ease-in-out infinite",
                        textShadow: "0 1px 2px #F5E748, 0 0 2px #F5E748",
                        marginLeft: "0.3rem",
                      }}
                    >
                       {rewardData.position || "..."} &nbsp;
                    </span>
                    <br />
                    You Earned:{" "}<br/>
                     <span
                      style={{
                        display: "inline-block",
                        animation: "bounceShadow 1.6s ease-in-out infinite",
                        textShadow: "0 1px 2px #F5E748, 0 0 2px #F5E748",
                        marginLeft: "0.3rem",
                      }}
                    >
                       {rewardData.reward || "..."} $USDC &nbsp;
                    </span>
                    <br />
                  </p>
                  {!hasSubmitted ? (
                    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto mt-8 px-6 sm:px-0">
                      <div className="mb-6">
                        <label htmlFor="blockchain" className="block text-lg 2xl:text-[1.2rem] font-semibold text-white mb-2">
                          Select Blockchain
                        </label>

                        <select
                        id="blockchain"
                        name="blockchain"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 text-[#1DA1F2] bg-white text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-[#1DA1F2]"
                        defaultValue="hedera"
                        >
                          <option value="hedera">Hedera</option>
                          <option value="polygon">Polygon</option>
                        </select>
                      </div>

                      <div className="mb-6">
                        <label htmlFor="address" className="block text-lg 2xl:text-[1.2rem] font-semibold text-white mb-2">
                          Wallet Address
                        </label>

                        <input
                        type="text"
                        id="address"
                        onChange={() => setAddressError("")}
                        name="address"
                        placeholder="0x..."
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 text-[#1DA1F2] bg-white text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-[#1DA1F2]"
                        />
                        {addressError && (
                          <p className="text-sm mt-2 text-red-500">{addressError}</p>
                        )}
                      </div>

                      <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto px-6 py-3 bg-[#1DA1F2] text-white font-semibold rounded-lg hover:bg-[#1A91DA] transition"
                      >
                        {loading ? "Loading..." : "Submit Address"}
                      </button>
                    </form>
                  ) : (
                    <p className="font-montserrat w-full sm:max-w-2xl mx-auto text-center md:text-justify font-normal px-6 lg:px-14 text-[1rem] 2xl:text-[1.6rem] 2xl:px-2 mt-6 lg:mt-10">
                     ✅ Wallet submitted successfully!
                     <br/>
                    </p>
                  )}

                  <style>
                    {`
                      @keyframes bounceShadow {
                        0%, 100% {
                          transform: translateY(0);
                        }
                        50% {
                          transform: translateY(-4px);
                        }
                      }
                    `}
                  </style>
                </aside>
              </div>

              <div className="trapezeInfinityJar absolute bg-[#25191E] w-full h-full top-0 hidden lg:block"></div>
              <div className="trapezePlusInfinityJar absolute bg-[#25191E] w-full h-full top-0 hidden lg:block"></div>
              <div className="trapezeInfinityJarMobile absolute bg-[#25191E] w-full h-full top-0 md:hidden"></div>
              <div className="trapezePlusInfinityJarMobile absolute bg-[#25191E] w-full h-full top-0 md:hidden"></div>
              <div className="trapezeInfinityJarIpad absolute bg-[#25191E] w-full h-full top-0 hidden md:block lg:hidden"></div>
              <div className="trapezePlusInfinityJarIpad absolute bg-[#25191E] w-full h-full top-0 hidden md:block lg:hidden"></div>

              </>
            ) : (
              <>
              <div className="relative lg:absolute top-6 sm:top-10 md:top-16 lg:top-24 xl:top-28 2xl:top-32 right-0 z-30 lg:mr-28 2xl:mr-20 3xl:mr-28">
                <h2 className="uppercase text-shadow--blue text-3xl sm:text-4xl md:text-5xl lg:text-[2.5rem] 2xl:text-6xl text-white font-bold italic text-center px-6 mt-4">
                  🏆 Welcome,{"  "}
                  <span className="text-inherit italic font-bold text-white">
                    <span
                    style={{
                      display: "inline-block",
                      animation: "bounceShadow 1.6s ease-in-out infinite",
                      textShadow: "0 1px 2px #F5E748, 0 0 2px #F5E748",
                    }}
                    >
                      @{twitterHandle || "loading"}
                    </span>
                  </span>
                </h2>

                <aside className="text-base text-white top-0 right-0">
                  <p className="font-montserrat w-full sm:max-w-2xl mx-auto text-center md:text-justify font-normal px-6 lg:px-14 text-[1rem] 2xl:text-[1.6rem] 2xl:px-2 mt-6 lg:mt-10">
                    Thank you for playing our first tournament
                    <br />
                    <br />
                    Unfortunately, you didn't win a reward this time.
                    <br />
                    <br />
                    ✅ Stay tuned for our next tournament,  {""}<br/>
                    <span
                      style={{
                        display: "inline-block",
                        animation: "bounceShadow 1.6s ease-in-out infinite",
                        textShadow: "0 1px 2px #F5E748, 0 0 2px #F5E748",
                        marginLeft: "0.3rem",
                      }}
                    >
                      coming soon &nbsp;
                    </span>
                  </p>

                  <style>
                    {`
                      @keyframes bounceShadow {
                        0%, 100% {
                          transform: translateY(0);
                        }
                        50% {
                          transform: translateY(-4px);
                        }
                      }
                    `}
                  </style>
                </aside>
              </div>

              <div className="trapezeInfinityJar absolute bg-[#25191E] w-full h-full top-0 hidden lg:block"></div>
              <div className="trapezePlusInfinityJar absolute bg-[#25191E] w-full h-full top-0 hidden lg:block"></div>
              <div className="trapezeInfinityJarMobile absolute bg-[#25191E] w-full h-full top-0 md:hidden"></div>
              <div className="trapezePlusInfinityJarMobile absolute bg-[#25191E] w-full h-full top-0 md:hidden"></div>
              <div className="trapezeInfinityJarIpad absolute bg-[#25191E] w-full h-full top-0 hidden md:block lg:hidden"></div>
              <div className="trapezePlusInfinityJarIpad absolute bg-[#25191E] w-full h-full top-0 hidden md:block lg:hidden"></div>
            </>
            )}
          {/* Animations */}
          <style>
            {`
              @keyframes bounceShadow {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-4px); }
              }
              @keyframes glow {
                0%, 100% { text-shadow: 0 0 1px #F5E748, 0 0 2px #F5E748; }
                50% { text-shadow: 0 0 2px #fff, 0 0 3px #F5E748; }
              }
            `}
          </style>
        </section>
      )}
    <Footer />
    </section>
  );
};

//-------------------------------------------------------------------------------------------------------------------
// Export CheckRewards Page
export default CheckRewards;

//-------------------------------------------------------------------------------------------------------------------
//--------------------------------End of CheckRewards.js-------------------------------------------------------------

